"use client"
import { Content, isFilled } from "@prismicio/client"
import { SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import {
  useForm,
  useStatus,
  useFormIsValid,
  useFormValues,
} from "@aura-design/system/form"
import { lostObjectFormSchema } from "@airplan/utils/validator-schema"
import { sendContactForm } from "@/utils/contact"
import { onFileUploadChange, uploadFile } from "@airplan/utils/files-form"
import {
  ALL_AIRPORTS,
  UIB_DEPENDENCIES,
} from "@airplan/utils/constants"
import Input from "@aura-design/system/input"
import Grid from "@aura-design/system/grid"
import Button from "@aura-design/system/button"
import Alert from "@aura-design/system/alert"
import Textarea from "@aura-design/system/textarea"
import Select from "@airplan/ui/select"
import useAirlines from "@airplan/hooks/airlines"
import useAirports from "@airplan/hooks/airports"

/**
 * Props for `FormLostObjects`.
 */
export type FormLostObjectsProps =
  SliceComponentProps<Content.FormLostObjectsSlice>

/**
 * Component for "FormLostObjects" Slices.
 */
const FormLostObjects = ({
  slice,
  context,
}: FormLostObjectsProps): JSX.Element => {
  const { PROJECT_UID } = context as any
  const formData = useForm({
    description_object: "",
    lastSeenTime: "",
    ownershipProof: "",
    address: "",
    date: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    airline: "",
    airport: PROJECT_UID,
    time: "",
    origin: "",
    destination: "",
    accept: false,
    file: "",
  })

  const status = useStatus()

  const {
    description_object,
    lastSeenTime,
    ownershipProof,
    address,
    date,
    firstName,
    email,
    phone,
    airline,
    airport,
    time,
    origin,
    destination,
    accept,
    file,
  } = formData

  const isValid = useFormIsValid(formData, lostObjectFormSchema)

  const handleContactForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    status.resetStatus()
    status.setIsLoading(true)
    if (!isValid) {
      for (const field in formData) {
        formData[field]?.setTouch(true)
      }
      status.resetStatus()
      status.setIsLoading(false)
      status.setMessage("Por favor revisa que todos los datos sean correctos")
      return
    }
    let publicUrl
    if (file?.value) {
      const resFile = await uploadFile(file.value)
      if (resFile.error) {
        status.setIsLoading(false)
        status.setIsError(true)
        status.setMessage(resFile.error)
        return
      }
      publicUrl = resFile.publicUrl
    }
    const formValues = useFormValues(formData)
    const res = await sendContactForm(
      { ...formValues, publicUrl },
      "lostObjects",
    )
    handleResponse(res)
  }

  const handleResponse = (res: any) => {
    if (res.status !== 200) {
      status.setIsError(true)
      status.setMessage("Ha ocurrido un error al enviar el correo electrónico")
      return
    }
    for (const field in formData) {
      formData[field]?.reset()
      formData[field]?.setTouch(false)
    }
    accept?.setValue(false)
    airport?.setValue(PROJECT_UID)
    status.setMessage("Tu mensaje ha sido enviado")
    status.setSubmited(true)
    status.setIsLoading(false)
  }
  const airlines = useAirlines(PROJECT_UID)
  const airports = useAirports(PROJECT_UID)

  return (
    <section
      className="lost-objects-form"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <Grid col="twelve" className="bg-dark-blue">
        <div className="span-9">
          <form onSubmit={handleContactForm}>
            <div className="text-white">
              <div className="smesh">
                <div className="pad">
                  <Grid col="two">
                    <div className="span-2">
                      <label>Descripción detallada del objeto*</label>
                      <Textarea {...description_object} />
                    </div>
                    <div className="span-2">
                      <label>
                        Cuándo y dónde fue la última vez que viste el objeto*
                      </label>
                      <Textarea {...lastSeenTime} />
                    </div>
                    <div className="span-2">
                      <label>
                        Qué te puede acreditar como dueño del objeto*
                      </label>
                      <Textarea {...ownershipProof} />
                    </div>
                    <div>
                      <label>Nombres y apellidos*</label>
                      <Input {...firstName} />
                    </div>
                    <div>
                      <label>Correo electrónico*</label>
                      <Input {...email} />
                    </div>
                    <div>
                      <label>Teléfono</label>
                      <Input {...phone} />
                    </div>
                    <div>
                      <label>Dirección</label>
                      <Input {...address} />
                    </div>
                    <div>
                      <label>Aerolínea</label>
                      <Select placeholder="Selecciona" {...airline}>
                        {airlines.data
                          ?.sort((a: any, b: any) =>
                            a.name.localeCompare(b.name),
                          )
                          ?.map((airline: any) => (
                            <option key={airline.id} value={airline.name}>
                              {airline.name}
                            </option>
                          ))}
                      </Select>
                    </div>
                    <div>
                      <label>Hora del vuelo</label>
                      <Input type="time" {...time} />
                    </div>
                    <div>
                      <label>Ciudad origen</label>
                      <Select placeholder="Selecciona" {...origin}>
                        {airports.data
                          ?.sort((a: any, b: any) =>
                            a.city.localeCompare(b.city),
                          )
                          ?.map((place: any) => (
                            <option key={place.city} value={place.city}>
                              {place.city}
                            </option>
                          ))}
                      </Select>
                    </div>
                    <div>
                      <label>Ciudad destino</label>
                      <Select placeholder="Selecciona" {...destination}>
                        {airports.data
                          ?.sort((a: any, b: any) =>
                            a.city.localeCompare(b.city),
                          )
                          ?.map((place: any) => (
                            <option key={place.city} value={place.city}>
                              {place.city}
                            </option>
                          ))}
                      </Select>
                    </div>
                    <div>
                      <label>Fecha del vuelo</label>
                      <Input type="date" {...date} />
                    </div>
                    <div>
                      {PROJECT_UID == "UIB" ? (
                        <label>Centro de Servicios*</label>
                      ) : (
                        <label>Aeropuerto*</label>
                      )}
                      {PROJECT_UID == "UIB" ? (
                        <Select placeholder="Selecciona" {...airport}>
                          {UIB_DEPENDENCIES.map((airport) => (
                            <option key={airport.value} value={airport.value}>
                              {airport.text}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Select
                          placeholder="Selecciona"
                          {...airport}
                          defaultValue={PROJECT_UID}
                          isDisabled={Boolean(PROJECT_UID)}
                        >
                          {ALL_AIRPORTS.map((airport) => (
                            <option key={airport.value} value={airport.value}>
                              {airport.text}
                            </option>
                          ))}
                        </Select>
                      )}
                    </div>
                    <div>
                      <em className="text-xs">Tamaño máximo: 20 MB</em>
                      <Input
                        type="file"
                        className="default"
                        {...file}
                        onChange={(event) => onFileUploadChange(event, file)}
                      />
                    </div>
                  </Grid>
                  <label className="flex items-center inputer-m-0 my-2">
                    <Input
                      className="default m-0 mr-1"
                      type="checkbox"
                      name="accept"
                      {...accept}
                      checked={!!accept?.value}
                      onChange={(e) => accept?.setValue(e.target.checked)}
                    />
                    Acepto la Política de Tratamiento de Datos Personales*
                  </label>
                  <Alert state={status.state} isPushBottom />
                  <div className="text-center">
                    <Button
                      label="Enviar"
                      isLoading={status.state.isLoading}
                      isLoadingText="Enviando..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div className="span-3 relative">
          <div className="sticky py-3">
            <div className="relative h-20 md:h-[100vh]">
              {isFilled.image(slice.primary.image) && (
                <PrismicNextImage
                  field={slice.primary.image}
                  className="rounded-xl object-cover"
                  imgixParams={{
                    txt: slice.primary.image?.copyright,
                  }}
                  fill
                  alt=""
                />
              )}
            </div>
          </div>
        </div>
      </Grid>
    </section>
  )
}

export default FormLostObjects
